// Umumiy kontakt reestri — `platform.contact` (ADR-031) bilan kontrakt.
//
//   GET  /api/contacts/by-owner/{ownerType}/{ownerId}   -> kontakt yoki 404
//   POST /api/contacts                                  -> ContactDraft
//   PUT  /api/contacts/{id}                             -> ContactFields
//   POST /api/contacts/{id}/reveal                      -> to'liq raqamlar
//
// Huquqlar: o'qish `contact.read`, yozish `contact.write`, raqamni ochish
// `contact.reveal` (XAVFLI toifa, sabab majburiy, auditga tushadi).
//
// ⚠️ TELEFON O'QISHDA NIQOBLANGAN keladi (`+9989****567`). `PUT` esa
// TO'LIQ ALMASHTIRISH — niqoblangan qiymatni qaytarib yuborish haqiqiy
// raqamni niqob bilan ALMASHTIRIB qo'yardi va buni hech narsa ushlamasdi:
// `contact_phone_shape` `*` belgisini taqiqlamaydi. Himoya
// {@link buildContactRequest} da, va u sinovda CHAQIRIB o'lchanadi.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'
// ⚠️ `platform.contact.contact_phone_shape` chegaralari BITTA joyda
// yozilgan va u yerdan olinadi — ikki nusxa ajrashib ketsa, bir panelda
// qabul qilingan raqam ikkinchisida rad etilardi.
export { PHONE_MAX, PHONE_MIN, PHONE_SAFE } from '@/features/agent/agentAccountApi'

export const CONTACT_OWNER_TYPES = ['PARTNER', 'STAFF', 'SUBSCRIBER', 'LEAD'] as const
export type ContactOwnerType = (typeof CONTACT_OWNER_TYPES)[number]

/** Backenddagi `ContactFields` uzunlik chegaralari */
export const FULL_NAME_MAX = 160
export const EMAIL_MAX = 190
export const ADDRESS_MAX = 240
export const NOTE_MAX = 500

/** Backenddagi `ContactFields.requireEmailShape` ning aynan o'zi */
export const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export interface ContactEntry {
  id: string
  ownerType: ContactOwnerType
  ownerId: string
  fullName: string | null
  /** ⚠️ NIQOBLANGAN */
  phone: string | null
  /** ⚠️ NIQOBLANGAN */
  phoneAlt: string | null
  email: string | null
  /** Reyestrgacha yozilgan erkin matn — `regionId` bo'lmagan qatorlarda */
  region: string | null
  district: string | null
  address: string | null
  note: string | null
  regionId: string | null
  regionName: string | null
  districtId: string | null
  districtName: string | null
}

const contactSchema = z.object({
  id: uuidSchema,
  ownerType: z.enum(CONTACT_OWNER_TYPES),
  ownerId: uuidSchema,
  fullName: z.string().nullish(),
  phone: z.string().nullish(),
  phoneAlt: z.string().nullish(),
  email: z.string().nullish(),
  region: z.string().nullish(),
  district: z.string().nullish(),
  address: z.string().nullish(),
  note: z.string().nullish(),
  regionId: uuidSchema.nullish(),
  regionName: z.string().nullish(),
  districtId: uuidSchema.nullish(),
  districtName: z.string().nullish(),
})

const revealSchema = z.object({
  id: uuidSchema,
  phone: z.string().nullish(),
  phoneAlt: z.string().nullish(),
})

export interface ContactReveal {
  phone: string | null
  phoneAlt: string | null
}

function toContact(raw: z.infer<typeof contactSchema>): ContactEntry {
  return {
    id: raw.id,
    ownerType: raw.ownerType,
    ownerId: raw.ownerId,
    fullName: raw.fullName ?? null,
    phone: raw.phone ?? null,
    phoneAlt: raw.phoneAlt ?? null,
    email: raw.email ?? null,
    region: raw.region ?? null,
    district: raw.district ?? null,
    address: raw.address ?? null,
    note: raw.note ?? null,
    regionId: raw.regionId ?? null,
    regionName: raw.regionName ?? null,
    districtId: raw.districtId ?? null,
    districtName: raw.districtName ?? null,
  }
}

/**
 * Egasi bo'yicha kontakt.
 *
 * ⚠️ 404 — XATO EMAS, «kontakt yo'q». Uni xato deb ko'rsatish mavjud
 * abonentlarning ko'pchiligida qizil banner chiqarardi: kontakt reestri
 * ular yaratilgandan KEYIN paydo bo'lgan.
 */
export async function fetchContactByOwner(
  ownerType: ContactOwnerType,
  ownerId: string,
): Promise<ContactEntry | null> {
  try {
    const response = await api.get<unknown>(`/api/contacts/by-owner/${ownerType}/${ownerId}`)
    return toContact(contactSchema.parse(response.data))
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null
    throw error
  }
}

export async function createContact(body: ContactCreateBody): Promise<ContactEntry> {
  const response = await api.post<unknown>('/api/contacts', body)
  return toContact(contactSchema.parse(response.data))
}

export async function updateContact(id: string, body: ContactBody): Promise<ContactEntry> {
  const response = await api.put<unknown>(`/api/contacts/${id}`, body)
  return toContact(contactSchema.parse(response.data))
}

/** ⚠️ Sabab MAJBURIY — sababsiz ochish auditni ma'nosiz qiladi. */
export async function revealContact(id: string, reason: string): Promise<ContactReveal> {
  const response = await api.post<unknown>(`/api/contacts/${id}/reveal`, { reason })
  const parsed = revealSchema.parse(response.data)
  return { phone: parsed.phone ?? null, phoneAlt: parsed.phoneAlt ?? null }
}

/**
 * Niqob belgisi.
 *
 * `Phones.mask` har doim `*` qo'yadi — qisqa raqam butunlay yulduzchaga
 * aylanadi, uzuni esa `+9989****567` bo'ladi. Ya'ni `*` ning bo'lishi
 * qiymat O'QISH yo'lidan kelganini bir qiymatli ko'rsatadi.
 */
export function isMaskedPhone(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.includes('*')
}

/**
 * Kiritilgan raqamni bir ko'rinishga keltiradi — backenddagi
 * `uz.xsib.common.contact.Phones#normalize` ning aynan o'zi.
 *
 * ⚠️ Mamlakat kodini TAXMIN QILMAYDI va shakl MAJBURLANMAYDI (GEO §7).
 */
export function normalizeContactPhone(raw: string | null | undefined): string | null {
  if (raw == null) return null
  // Belgilar ESCAPE bilan yozilgan: uzilmas probel va uzun tirelar ekranda
  // oddiylaridan farq qilmaydi va tasodifan o'chib ketardi.
  const cleaned = raw.trim().replace(/[\s\u00A0()\-\u2010-\u2015.]/g, '')
  if (!cleaned) return null
  return cleaned.startsWith('00') ? `+${cleaned.slice(2)}` : cleaned
}

/** Formadagi kontakt maydonlari — abonent formasida `contact.*` ostida */
export interface ContactFieldValues {
  fullName?: string
  phone?: string
  phoneAlt?: string
  email?: string
  regionId?: string | null
  districtId?: string | null
  address?: string
  note?: string
}

export interface ContactBody {
  fullName: string | null
  phone: string | null
  phoneAlt: string | null
  email: string | null
  region: string | null
  district: string | null
  address: string | null
  note: string | null
  regionId: string | null
  districtId: string | null
}

export interface ContactCreateBody extends ContactBody {
  ownerType: ContactOwnerType
  ownerId: string
}

export type ContactRequest =
  | { kind: 'none' }
  | { kind: 'blocked'; reason: 'PHONE_MASKED' | 'EMPTY' }
  | { kind: 'create'; body: ContactCreateBody }
  | { kind: 'update'; id: string; body: ContactBody }

interface BuildInput {
  ownerType: ContactOwnerType
  ownerId: string
  values: ContactFieldValues
  /** Serverdan o'qilgan joriy kontakt yoki `null` — hali yo'q */
  current: ContactEntry | null
}

function text(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function isEmpty(body: ContactBody): boolean {
  return Object.values(body).every((value) => value === null)
}

function sameBody(body: ContactBody, current: ContactEntry): boolean {
  return (
    body.fullName === current.fullName &&
    body.phone === current.phone &&
    body.phoneAlt === current.phoneAlt &&
    body.email === current.email &&
    body.region === current.region &&
    body.district === current.district &&
    body.address === current.address &&
    body.note === current.note &&
    body.regionId === current.regionId &&
    body.districtId === current.districtId
  )
}

/**
 * ABONENT + KONTAKT ZANJIRINING YAGONA SHAKL NUQTASI.
 *
 * ⚠️ So'rov tanasi FAQAT shu yerda quriladi. Backendda zanjir hali
 * yozilyapti (kontakt abonent so'roviga qo'shilishi ham mumkin), ya'ni
 * shakl o'zgarsa AYNAN SHU funksiya o'zgaradi — chaqiruv joylari emas.
 *
 * <h2>⚠️ Uchta qoida shu yerda qotirilgan</h2>
 *
 * <b>1. Niqoblangan raqam HECH QACHON qaytib yuborilmaydi.</b> `PUT`
 * to'liq almashtirish, ya'ni `+9989****567` ni yuborish bazadagi haqiqiy
 * raqamni O'CHIRIB, o'rniga niqobni yozardi. Server buni rad etmaydi:
 * `contact_phone_shape` faqat uzunlik va xavfli belgini tekshiradi.
 *
 * <b>2. To'ldirilmagan maydon «o'chirildi» degani EMAS.</b> Reyestrgacha
 * yozilgan erkin matn (`region`/`district`) tanlagichda ko'rinmaydi —
 * u tanlov O'ZGARMAGAN bo'lsa o'zgarishsiz qaytariladi. Aks holda
 * birinchi saqlash eski hududni jimgina yo'q qilardi.
 *
 * <b>3. Bo'sh kontakt YARATILMAYDI.</b> Butunlay bo'sh yozuv birorta
 * cheklovni buzmaydi, lekin `contact_owner_uq` tufayli EGANI band qilib
 * qo'yadi va keyingi haqiqiy kontakt 409 olardi.
 */
export function buildContactRequest(input: BuildInput): ContactRequest {
  const { ownerType, ownerId, values, current } = input

  const phone = normalizeContactPhone(values.phone)
  const phoneAlt = normalizeContactPhone(values.phoneAlt)

  if (isMaskedPhone(phone) || isMaskedPhone(phoneAlt)) {
    return { kind: 'blocked', reason: 'PHONE_MASKED' }
  }

  const regionId = values.regionId ?? null
  const districtId = values.districtId ?? null

  const body: ContactBody = {
    fullName: text(values.fullName),
    phone,
    phoneAlt,
    email: text(values.email),
    // ⚠️ Eski erkin matn: tanlov o'zgarmagan bo'lsa SAQLANADI, o'zgargan
    // bo'lsa reyestrdagi ID yagona manbaga aylanadi va matn tushadi.
    region: regionId === (current?.regionId ?? null) ? (current?.region ?? null) : null,
    district: districtId === (current?.districtId ?? null) ? (current?.district ?? null) : null,
    address: text(values.address),
    note: text(values.note),
    regionId,
    districtId,
  }

  if (current === null) {
    if (isEmpty(body)) return { kind: 'none' }
    return { kind: 'create', body: { ...body, ownerType, ownerId } }
  }

  if (isEmpty(body)) return { kind: 'blocked', reason: 'EMPTY' }
  if (sameBody(body, current)) return { kind: 'none' }
  return { kind: 'update', id: current.id, body }
}

/** Serverdagi kontaktni forma qiymatlariga o'giradi */
export function toContactValues(contact: ContactEntry | null): ContactFieldValues {
  return {
    fullName: contact?.fullName ?? '',
    phone: contact?.phone ?? '',
    phoneAlt: contact?.phoneAlt ?? '',
    email: contact?.email ?? '',
    regionId: contact?.regionId ?? null,
    districtId: contact?.districtId ?? null,
    address: contact?.address ?? '',
    note: contact?.note ?? '',
  }
}
